import React, { useMemo } from 'react';
import type { AreaCodeGapRow } from '../../../services/phoneNumberHealthService';
import PhoneNumbersSection from './PhoneNumbersSection';
import { sortGapsForDisplay } from './healthDashboardUtils';
import UtilizationBar from './UtilizationBar';

const STATUS_STYLE: Record<string, string> = {
  healthy: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  almost_maxed: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  maxed_out: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  need_more: 'bg-red-100 text-red-900 dark:bg-red-900/40 dark:text-red-300',
  warning: 'bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-300',
};

interface Props {
  rows: AreaCodeGapRow[];
  periodLabel?: string;
  onPurchaseArea?: (areaCode: string) => void;
}

const AreaCodeGapTable: React.FC<Props> = ({ rows, periodLabel = 'Today', onPurchaseArea }) => {
  const sorted = useMemo(() => sortGapsForDisplay(rows), [rows]);
  const needMore = sorted.filter((r) => r.missingNumbers > 0 || r.status === 'need_more');
  const displayRows = (needMore.length > 0 ? needMore : sorted).slice(0, 25);

  return (
  <div id="area-code-gaps-table">
  <PhoneNumbersSection
    title="Area Code Gaps"
    description={`Lead demand vs. capacity for ${periodLabel.toLowerCase()}. Red rows may need new purchases.`}
    noPadding
  >
    {displayRows.length === 0 ? (
      <p className="text-sm text-muted-foreground text-center py-10 px-4">No area code gaps detected.</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-3 font-medium">Area Code</th>
              <th className="px-4 py-3 font-medium">Leads</th>
              <th className="px-4 py-3 font-medium">Active #s</th>
              <th className="px-4 py-3 font-medium">Daily Cap</th>
              <th className="px-4 py-3 font-medium">Used ({periodLabel})</th>
              <th className="px-4 py-3 font-medium">Utilization</th>
              <th className="px-4 py-3 font-medium">Buy</th>
              <th className="px-4 py-3 font-medium">Missing</th>
              <th className="px-4 py-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {displayRows.map((r) => (
              <tr key={r.areaCode} className="border-t border-border/60 hover:bg-muted/20 transition-colors">
                <td className="px-4 py-3">
                  <button
                    type="button"
                    className="font-semibold text-primary hover:underline"
                    onClick={() => onPurchaseArea?.(r.areaCode)}
                    title="Purchase number in this area code"
                  >
                    {r.areaCode}
                  </button>
                  <p className="text-[10px] text-muted-foreground">{r.timezone}</p>
                </td>
                <td className="px-4 py-3 tabular-nums">{r.totalCallableLeads}</td>
                <td className="px-4 py-3 tabular-nums">{r.activeNumbersOwned}</td>
                <td className="px-4 py-3 tabular-nums">{r.dailyCapacity}</td>
                <td className="px-4 py-3 tabular-nums">{r.usedCapacityToday}</td>
                <td className="px-4 py-3">
                  <UtilizationBar
                    used={r.usedCapacityToday}
                    capacity={r.dailyCapacity}
                    percent={r.utilizationPercent}
                  />
                </td>
                <td className="px-4 py-3 tabular-nums text-xs">
                  {r.suggestedPurchaseCount != null ? (
                    <button
                      type="button"
                      className="font-semibold text-primary hover:underline"
                      onClick={() => onPurchaseArea?.(r.areaCode)}
                    >
                      +{r.suggestedPurchaseCount}
                    </button>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="px-4 py-3 tabular-nums font-medium">{r.missingNumbers}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${STATUS_STYLE[r.status] ?? 'bg-muted text-muted-foreground'}`}>
                    {r.status.replace(/_/g, ' ')}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </PhoneNumbersSection>
  </div>
  );
};

export default AreaCodeGapTable;
